import React from "react";
import {connect, useSelector} from "react-redux";
import {AppState} from "../RootReducer";
import {
    BonusConfig,
    BonusLogicType,
    BonusOffer,
    ContributionProduct,
    MonetaryConfig,
    TurnoverContributions,
    UserBonusView
} from "../http/protocol";
import {Big} from "big.js";
import CurrencyFormatter from "../utils/CurrencyFormatter";
import {loadCasinoGames} from "../casino/CasinoActions";
import {CasinoState, CasinoStatus} from "../casino/CasinoReducer";
import {getContainer} from "../ioc/IOCSetup";
import "./BonusPage.scss";
import {t} from "../i18n";
import {getIconFromProduct} from "./NextBonuses";
import {AccountCard} from "../ui/AccountUI";
import {formatDate} from "../utils/date";
import ActiveBonusLink from "../ui/ActiveBonusLink";


interface BonusDetailProps {
    bonusConfig?: BonusConfig;
    bonusActive?: UserBonusView;
    bonusOffer?: BonusOffer;
    mc?: MonetaryConfig;
    expires?: number;
    currencyCode?: string;
    // currency?: CurrencyDefinition;
    tc?: TurnoverContributions;
    award?: boolean;
    nextBonusExpires?: number | Date;
    bonusNameSlug?: string;
}

export const BonusDetailPanel = ( props : BonusDetailProps) => {
    const currency = useSelector((app: AppState) => app.settings?.settings?.currencies?.find((c) => c.code == props.mc?.currencyCode))

    if(!props.mc || !props.expires || !currency  ||  !props.bonusConfig) {
        return <div></div>
    }

    const type = props.bonusConfig.type;
    const running = !!props.bonusActive

    let turnoverRequirementElement;
    if (running) {
        turnoverRequirementElement = <span>{CurrencyFormatter.formatWithSymbol(currency, Big(props.bonusActive!.turnoverRequirement?? 0))} ({props.mc.turnoverRequirementFactor}x)</span>
    } else {
        const turnoverAmount = Big(props.bonusOffer?.turnoverRequirement ?? 0)

        if (turnoverAmount.gt(0)) {
            turnoverRequirementElement = <span>{CurrencyFormatter.formatWithSymbol(currency, Big(props.bonusOffer?.turnoverRequirement ?? 0))} ({props.mc.turnoverRequirementFactor}x)</span>
        } else {
            turnoverRequirementElement = <span>{props.mc.turnoverRequirementFactor}x</span>
        }
    }


    let inputAmountParachute = props.bonusConfig.monetaryConfig?.inputAmountAsBonusMoney && type == BonusLogicType.PARACHUTE;
    return (
        <div className={`bonus-details ${props.bonusNameSlug && props.bonusNameSlug}`}>
            <div className={"details-item type"}>{t(`bonus.terms.type`)} <span>{t(`bonus.terms.${type}.type`)}</span></div>
            {inputAmountParachute && (
                <div className={"details-item description"}>{t(`bonus.terms.description`)} <span>{t(`bonus.terms.${type}.input-as-bonus-description`)}</span></div>
            )}
            {!inputAmountParachute && (
                <div className={"details-item description"}>{t(`bonus.terms.description`)} <span>{t(`bonus.terms.${type}.description`)}</span></div>
            )}
            {props.award && props.nextBonusExpires && (<div className={"details-item expires"}>{t("bonus.terms.expires")} <span>{formatDate(props.nextBonusExpires)}</span></div>)}

            <div className={"details-item max-wager"}>{t("bonus.terms.max-wager-amount")} <span>{CurrencyFormatter.formatWithSymbol(currency, Big(props.mc.maxWagerAmount ?? 0))}</span></div>

            { !running  &&
                <>
                    <div className={"details-item days"}>{t("bonus.terms.days")} <span>{props.expires}</span></div>
                    { !props.bonusOffer  &&
                        <>
                            <div className={"details-item max-amount"}>{t("bonus.terms.max-amount")} <span>{CurrencyFormatter.formatWithSymbol(currency, Big(props.mc.maxInputAmount ?? 0))}</span></div>
                            <div className={"details-item bonus-amount"}>{t("bonus.terms.bonus-amount")} <span>{Big(props.mc.awardAmountFraction ? props.mc.awardAmountFraction : 0).times(100).toString() + "%"}</span></div>
                        </>
                    }
                    {props.bonusOffer &&
                        <div className={"details-item bonus-amount"}>{t("bonus.terms.bonus-amount")} <span>{CurrencyFormatter.formatWithSymbol(currency, Big(props.bonusOffer.amount ?? 0))}</span></div>
                    }
                    <div className={"details-item turnover"}>{t("bonus.terms.turnover-requirement")} {turnoverRequirementElement}</div>
                </>
            }

            { running  &&
                <>
                    <div className={"details-item expires"}>{t("bonus.terms.expires")} <span>{formatDate(props.bonusActive!.expires)}</span></div>
                    <div className={"details-item bonus-amount"}>{t("bonus.terms.bonus-amount")} <span>{CurrencyFormatter.formatWithSymbol(currency, Big(props.bonusActive!.totalBonusAmount ?? 0))}</span></div>
                    <div className={"details-item turnover"}>{t("bonus.terms.turnover-requirement")} {turnoverRequirementElement}</div>
                </>
            }

            <h5 className={"bonus-terms-title"}>
                <i className={"material-icons"}>check</i> {t("bonus.bonusIncludedGames")}
            </h5>

                {
                props.tc && props.tc.contributions && props.tc.contributions.map( c => (
                    <AccountCard key={c.id} className={"product-contribution row"}>
                        <div className={"col-md-2 icon-container"}>
                            <i className={getIconFromProduct(c.product).className}>{getIconFromProduct(c.product).text}</i>
                        </div>
                        <div className={"col-md-10"}>
                            <h6 className={"title"}>{c.description}</h6>
                            {(c.product == ContributionProduct.SPORTSBOOK || c.product == ContributionProduct.RACING) && (
                                <div>
                                    <div className={"details-item"}>{t("bonus.terms.contribution")} <span>{c.contributionFactor ? Big(c.contributionFactor).times(100).toString() + "%" : "N/A"}</span></div>
                                    <div className={"details-item"}>{t("bonus.terms.min-odds")} <span>{c.minOdds}</span></div>
                                    <div className={"details-item"}>{t("bonus.terms.max-odds")} <span>{c.maxOdds}</span></div>
                                    <div className={"details-item"}>{t("bonus.terms.live-betting")} <span>{c.liveBetting ? "Yes" : "No"}</span></div>
                                    <div className={"details-item"}>{t("bonus.terms.pre-match-betting")} <span>{c.preMatchBetting ? "Yes" : "No"}</span></div>
                                </div>
                            )}

                            {c.product == ContributionProduct.CASINO && (
                                <div>
                                    <div className={"details-item"}>{t("bonus.terms.contribution")} <span>{c.contributionFactor ? Big(c.contributionFactor).times(100).toString() + "%" : "N/A"}</span></div>
                                    <ActiveBonusLink />
                                    <div className={"details-item"}>{t("bonus.terms.included-games")} <span>{props.bonusConfig?.id && c.id && <BonusGamesList contributionId={c.id} bonusConfigId={props.bonusConfig.id}/>}</span></div>
                                </div>
                            )}

                            {c.product == ContributionProduct.POKER && (
                                <div>
                                    <div className={"details-item"}>{t("bonus.terms.contribution")} <span>{c.contributionFactor ? Big(c.contributionFactor).times(100).toString() + "%" : "N/A"}</span></div>
                                </div>
                            )}
                        </div>
                    </AccountCard>
                ))}

        </div>
    )
};

interface BonusGamesListProps {
    bonusConfigId? : number
    contributionId? : number;
    casino? : CasinoState;
    loadCasinoGames? : () => void;
}

export class BonusGamesListImpl extends React.Component<BonusGamesListProps, { loading?: boolean, gameIds?: {[k:number]:number[]}, showAll?: boolean}> {
    constructor(props : BonusGamesListProps) {
        super(props);
        this.state = { loading : true, showAll : false};
    }
    componentDidMount() {
        if(this.props.casino && this.props.casino.status != CasinoStatus.DONE) {
            this.props.loadCasinoGames && this.props.loadCasinoGames();
        } else {
            this.loadGameIds();
        }
    }
    componentDidUpdate(prevProps: Readonly<BonusGamesListProps>, prevState: Readonly<{ loading?: boolean }>, snapshot?: any) {
        if(this.props.casino && this.props.casino.status == CasinoStatus.DONE && (prevProps.casino && prevProps.casino.status != CasinoStatus.DONE)) {
            this.loadGameIds();
        }
    }

    loadGameIds() {
        if(this.props.bonusConfigId) {
            getContainer().getUserBonusService().getCasinoGamesByBonus(this.props.bonusConfigId).then( r => {
                this.setState({ gameIds : r, loading : false});
            });
        }
    }

    render() {
        if(this.state.loading) {
            return <span>Loading...</span>
        }
        if(!this.props.casino || !this.props.casino.lobby || !this.props.casino.lobby.games) {
            return <div></div>
        }
        if(!this.state.gameIds || !this.props.contributionId) {
            return <div></div>
        }
        let gameIdsByContribution = this.state.gameIds[this.props.contributionId];
        if(!this.state.showAll) {
            gameIdsByContribution = gameIdsByContribution.slice(0,Math.min(Object.values(this.props.casino.lobby.games ?? {}).length, 10));
        }
        if(this.props.contributionId && gameIdsByContribution) {
            return (
                <span>
                    <span>{Object.values(this.props.casino.lobby.games ?? {}).filter( g => gameIdsByContribution.some( (id : number) => id == g.id)).map(g => g.name).join(", ")}</span>
                    {!this.state.showAll && <a href="#" onClick={()=> this.setState({ showAll : true})}> {t("bonus.showAll")} </a>}
                    {this.state.showAll && <a href="#" onClick={()=> this.setState({ showAll : false})}> {t("bonus.showLess")} </a>}

                </span>
            )
        }
        return <div/>

    }
}
const BonusGamesList = connect((app : AppState) => {
    return {
        casino : app.casino,

    }
},dispatch => {
    return {
        loadCasinoGames : () => dispatch(loadCasinoGames() as any)
    }
})(BonusGamesListImpl) as any;
